/**
 * Quote engine — the seam between the public satellite-quoting UI
 * and whichever backend produces the actual area measurement and price.
 *
 * Stage 2 layout:
 *   - QuoteEngine: the contract every implementation satisfies.
 *   - MockQuoteEngine: returns deterministic canned data so we can ship
 *     the UI before Google Maps + Claude vision keys land.
 *   - The real implementation (lives in an Edge Function) calls Google
 *     Maps Static API for the satellite tile, then Claude vision for
 *     the lawn-area measurement, then the service catalog for pricing.
 *     Drops in by satisfying the same interface — no UI changes needed.
 */

export interface QuoteAddressInput {
  /** Single-line address from the prospect (will be normalized later). */
  raw: string;
  /** Optional pre-resolved place data when Google Places autocomplete runs. */
  placeId?: string;
}

export interface QuoteLineItem {
  serviceId?: string;
  name: string;
  /** USD, not cents. The DB stores numeric(10,2). */
  price: number;
  /** Free-form description shown under the line item. */
  detail?: string;
}

export interface QuoteResult {
  /** Echoed normalized address (one-line). */
  formattedAddress: string;
  /** Lat/long the address resolved to, when available. */
  latitude?: number;
  longitude?: number;
  /** Total lawn area measured by satellite vision, in square feet. */
  lawnAreaSqft: number;
  /** Total parcel area in square feet (lawn + driveway + structures + beds). */
  totalAreaSqft?: number;
  /** URL of the annotated satellite image; may be a data URL for mocks. */
  satelliteImageUrl: string;
  /** Itemized price breakdown. */
  lineItems: QuoteLineItem[];
  /** Sum of line item prices in USD. */
  totalPrice: number;
  /** Sentinel: identifies which implementation produced this quote. */
  engine: "mock" | "google+claude" | string;
  /** Wall-clock ms it took the engine to produce this result. */
  durationMs: number;
}

export interface QuoteEngine {
  quoteFromAddress(input: QuoteAddressInput): Promise<QuoteResult>;
}

/**
 * MockQuoteEngine — deterministic results derived from the input string,
 * so a given address always returns the same quote during development.
 */
export class MockQuoteEngine implements QuoteEngine {
  async quoteFromAddress(input: QuoteAddressInput): Promise<QuoteResult> {
    const started = Date.now();
    // Simulate network latency so the UI loading state is visible.
    await new Promise((r) => setTimeout(r, 800));

    const seed = hashString(input.raw.toLowerCase().trim() || "default");
    // Lawn area between 2,500 and 22,500 sq ft (typical residential range).
    const lawnAreaSqft = 2500 + (seed % 20_000);
    const totalAreaSqft = lawnAreaSqft + 1500 + (seed % 3000);

    // Reasonable defaults: $35 base mow + $0.012 / sq ft (gives a $65 quote
    // on a 2,500 sqft lawn and a $305 quote on a 22,500 sqft lawn).
    const mowBase = 35;
    const mowPerSqft = 0.012;
    const mowPrice = Math.round((mowBase + lawnAreaSqft * mowPerSqft) * 100) / 100;

    const lineItems: QuoteLineItem[] = [
      {
        name: "Weekly mowing",
        price: mowPrice,
        detail: `${lawnAreaSqft.toLocaleString()} sq ft @ $${mowPerSqft}/sq ft + $${mowBase} base`,
      },
    ];
    // Throw in an aeration upsell on larger lawns.
    if (lawnAreaSqft > 8000) {
      lineItems.push({
        name: "Spring aeration (recommended)",
        price: 195,
        detail: "Add-on; declinable at checkout.",
      });
    }

    return {
      formattedAddress: input.raw.trim(),
      lawnAreaSqft,
      totalAreaSqft,
      satelliteImageUrl: buildPlaceholderSatelliteSvg(seed),
      lineItems,
      totalPrice: round2(lineItems.reduce((sum, li) => sum + li.price, 0)),
      engine: "mock",
      durationMs: Date.now() - started,
    };
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/**
 * Inline SVG that *looks* like an annotated satellite tile, with a colored
 * polygon overlay representing the "detected lawn." Lets the UI feel real
 * before the real Google Maps imagery lands.
 */
function buildPlaceholderSatelliteSvg(seed: number): string {
  const hue = seed % 360;
  // Lawn polygon — pseudo-random but stable for this seed
  const cx = 200 + (seed % 60) - 30;
  const cy = 150 + ((seed >> 4) % 40) - 20;
  const rx = 90 + ((seed >> 8) % 40);
  const ry = 60 + ((seed >> 12) % 30);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='480' height='320' viewBox='0 0 480 320'>` +
    `<defs>` +
    `<linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0' stop-color='hsl(${hue},35%,28%)'/>` +
    `<stop offset='1' stop-color='hsl(${(hue + 35) % 360},25%,18%)'/>` +
    `</linearGradient>` +
    `</defs>` +
    `<rect width='480' height='320' fill='url(#g)'/>` +
    `<ellipse cx='${cx}' cy='${cy}' rx='${rx}' ry='${ry}' fill='hsl(120,55%,40%)' fill-opacity='0.65' stroke='hsl(85,100%,75%)' stroke-width='2'/>` +
    `<text x='14' y='24' fill='white' font-family='monospace' font-size='14' opacity='0.85'>SATELLITE PREVIEW (MOCK)</text>` +
    `<text x='14' y='306' fill='white' font-family='monospace' font-size='11' opacity='0.75'>Real Google Maps imagery once API keys land</text>` +
    `</svg>`;
  // btoa is available in browsers and Node 16+ (and Deno).
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
